import { motion } from "motion/react"
import { useEffect, useState } from "react"
import { useLocation, useNavigate } from "react-router"

import { parseNumber } from "@forento/core/utilities/number"

import Button from "~/components/Button"
import LoadingIndicator from "~/components/LoadingIndicator"
import { useUser } from "~/contexts/UserContext"
import routes from "~/utilities/routes"
import trpc from "~/utilities/trpc"

import Layout, { Logo } from "./Layout"
import { authCardClass, staggerContainer, staggerItem } from "./Layout/authCard"

const VerifyEmailPage: React.FC = () => {
	const user = useUser()
	const params = new URLSearchParams(useLocation().search)
	const navigate = useNavigate()

	const userId = parseNumber(params.get("user") ?? "")
	const token = params.get("token")

	const [state, setState] = useState<"loading" | "failed" | "success">("loading")

	useEffect(() => {
		async function verify() {
			if (userId === null || token === null) {
				navigate(routes.main.index(), { replace: true })
				return
			}
			const { status } = await trpc.user.verifyEmail.mutate({ userId, token })
			setState(status === "success" ? "success" : "failed")
			await user.reload()
		}
		verify()
	}, [navigate, token, user, userId])

	return (
		<Layout>
			<Logo />
			<div className={authCardClass}>
				{state === "loading" ? (
					<div className="flex flex-col items-center gap-4 py-8">
						<LoadingIndicator />
						<p className="text-sm text-zinc-500">Verifying email address...</p>
					</div>
				) : (
					<motion.div
						className="flex w-full flex-col items-stretch gap-0"
						variants={staggerContainer}
						initial="hidden"
						animate="visible"
					>
						<motion.div variants={staggerItem}>
							<h1 className="text-left text-xl font-bold tracking-tight text-zinc-900">
								{state === "failed" ? "Verification failed" : "Check your email"}
							</h1>
							<p className="mt-1.5 text-left text-sm text-zinc-500">
								{state === "failed"
									? "Failed to verify email address. The link may be expired. Please try again."
									: "Your email address is now verified!"}
							</p>
						</motion.div>
						{state === "success" && (
							<motion.div variants={staggerItem} className="mt-6">
								<Button
									variant="primary"
									onClick={routes.main.index()}
									className="w-full cursor-pointer rounded-xl py-3 text-sm font-semibold"
								>
									Go to dashboard
								</Button>
							</motion.div>
						)}
					</motion.div>
				)}
			</div>
		</Layout>
	)
}

export default VerifyEmailPage
